import { motion } from '@carbon/motion';
import { omit } from 'lodash';
import { AnchorHTMLAttributes } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { getHoverColor } from '../../../global/styling-helpers';
import { WithClassName } from '../../../types/types';

interface InternalLinkProps extends WithClassName, LinkProps {
  color?: string;
  isBold?: boolean;
}

export function BouLink(props: InternalLinkProps) {
  const linkAndClassProps = omit(props, 'color', 'isBold');
  return (
    <StyledLink {...linkAndClassProps} to={props.to} color={props.color} data-isbold={props.isBold ? 'true' : 'false'}>
      {props.children}
    </StyledLink>
  );
}

/**
 * OA 10/2022: Due to a hook error occuring from Styled Components 5.0.1 onwards, I had to downgrade it to 5.0.0
 * after updating to React v18.2.0. Unfortunately, Styled Components 5.0.0 doesn't properly handle transient props
 * (those starting with a dollar sign), and hence the $isBold prop gets passed to the html element, resulting
 * in a console error from React. React allows passing all lowercase props, so I changed the props of the styled components
 * and built a separate wrapper for the BouLinkExternal to handle this. Let's hope we can untangle this in future versions of
 * Styled Components and/or React.
 *
 * Additional note 10/2022: To suppress DOM warnings, we also need to pass the values to the DOM as strings that begin with "data-". This
 * here is a very ugly workaround, hopefully we can fix this soon when the aforementioned packages update.
 */

const StyledLink = styled(Link)<{ color?: string; 'data-isbold': 'true' | 'false' }>`
  ${props => getBouLinkStyle(props['data-isbold'] === 'true', props.color)}
`;

export interface ExternalLinkProps extends WithClassName, AnchorHTMLAttributes<HTMLAnchorElement> {
  color?: string;
  isBold?: boolean;
  disableUnderLine?: boolean;
}

export const BouLinkExternal = (props: ExternalLinkProps) => {
  const linkAndClassProps = omit(props, 'color', 'isBold');
  return (
    <StyledExternalLink
      {...linkAndClassProps}
      href={props.href}
      data-isbold={props.isBold ? 'true' : 'false'}
      color={props.color}
      title={linkAndClassProps.title || props.href}
    />
  );
};

const StyledExternalLink = styled.a<{ color?: string; 'data-isbold': 'true' | 'false' }>`
  ${props => getBouLinkStyle(props['data-isbold'] === 'true', props.color)}
`;

export function getBouLinkStyle(isBold?: boolean, color?: string, hoverTheme: 'lighter' | 'darker' = 'lighter') {
  const hoverColor = getHoverColor(color, hoverTheme);

  return css`
    text-decoration: none;
    color: ${color || 'currentColor'};
    &:hover {
      cursor: pointer;
      color: ${hoverColor || 'currentColor'} !important;
    }
    &:active {
      color: ${color || 'currentColor'};
    }
    &:visited {
      color: ${color || 'currentColor'};
    }
    font-weight: ${isBold && 'bold'};
    transition: color 70ms ${motion('standard', 'productive')};
  `.join('');
}
